import { observable, computed } from 'mobx';

// 初始化发帖数据
const initPostData = {
    id: null,            // 编辑模式下的帖子ID
    type: 0,
    title: '',
    content: '',
    source: 'PC',
    coverId: 0,
    videoId: 0,
    topicIds: [],
    collection:[],// 关联帖子
    mentions: [],
    votes: [],
    form: null,          // 表单数据
    isAigc: false, // 内容声明：AI生成
    hasCopyright: false, // 是否设置版权保护
    publishMode: 'immediate', // 发布模式：'immediate' 立即发布，'scheduled' 定时发布
    fixedAt: null,       // 定时发布时间（ISO 8601 格式或 Unix 时间戳）
    draft: 0, // 草稿标记 0-发布 1-保存草稿
    // 问题反馈字段（type === 2）
    problemId: '',
    problemName: '',
    problemSolvedCount: 0,
    problemSolvedCountLoaded: false,
    productId: '',
    productName: '',
    systemVersion: '',
    imei: '',
    phone: '',
    logCode: '',
    // 附件相关
    coverImage: '',       // 封面图本地路径
    coverImageUploaded: false, // 封面图是否已上传
    video: '',            // 视频本地路径
    videoUploaded: false, // 视频是否已上传
    videoFile: null,      // 视频文件对象
    videoCoverUrl: '',    // 视频自动生成的封面 URL
    videoCoverData: null, // 完整的视频数据
    // 上传结果存储
    uploadedAttachments: [], // 已上传的附件ID数组 [{ type: 1, id: 123, url: '' }]
};

const getVoteOptionContent = (option) => {
    if (!option || typeof option !== 'object') return '';
    return (option.content || option.text || '').trim();
};

const isConfiguredVote = (vote) => {
    if (!vote || typeof vote !== 'object') return false;
    if (typeof vote.id === 'number') return true;

    const title = (vote.title || '').trim();
    if (title) return true;

    return Array.isArray(vote.options) && vote.options.some(option => !!getVoteOptionContent(option));
};

const getConfiguredVotes = (votes) => {
    if (!Array.isArray(votes)) return [];
    return votes.filter(isConfiguredVote);
};

// 发帖 Store - 管理发帖相关的状态
class SetPostStore {
    /**
     * 发帖数据
     */
    @observable postData = { ...initPostData };

    /**
     * 加载状态
     */
    @observable loading = {
        submit: false,
        uploadCover: false,
    };

    /**
     * 日志记录（内存 + 可选持久化）
     */
    @observable postLogs = [];

    /**
     * 草稿相关状态
     */
    @observable draftData = {
        hasDraft: false,        // 是否有草稿
        draftThreadId: null,    // 草稿的 threadId
        isRestoreDraft: false,  // 是否从草稿恢复
        lastSaveType: 'manual',   // 最后一次保存类型：'manual'=手动
    };

    /**
     * 话题缓存 - 存储已获取的话题详情，用于显示真实的话题名称
     */
    @observable topicCache = {};

    addLog(event, data = {}) {
        try {
            const entry = {
                ts: new Date().toISOString(),
                event,
                data,
            };
            // setPostData 高频触发，不写入 observable，避免 @observer 页面整页重渲染
            const skipObservableLog = event === 'setPostData';
            if (!skipObservableLog) {
                this.postLogs = [...this.postLogs, entry].slice(-500);
            }
            if (typeof window !== 'undefined' && window.localStorage && !skipObservableLog) {
                const key = 'setPostLogs';
                const existing = JSON.parse(window.localStorage.getItem(key) || '[]');
                existing.push(entry);
                window.localStorage.setItem(key, JSON.stringify(existing.slice(-500)));
            }
            /* eslint-disable no-console */
            console.log(`[SetPostLog] ${event}`, entry);
        } catch (e) {
            // 静默失败，避免影响主流程
        }
    }

    clearLogs() {
        this.postLogs = [];
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                window.localStorage.removeItem('setPostLogs');
            }
        } catch (e) {}
    }

    /**
     * 验证发帖数据是否有效
     */
    @computed get isPostDataValid() {
        const { type, title, content } = this.postData;
        const contentOk = !!(content && content.trim());
        const titleOk = type === 0 ? !!(title && title.trim() && title.length <= 30) : true; // type=1 不校验标题
        return titleOk && contentOk;
    }

    /**
     * 验证投票数据是否有效
     */
    @computed get isVoteDataValid() {
        const configuredVotes = getConfiguredVotes(this.postData.votes);
        if (configuredVotes.length === 0) return true; // 投票是可选的
        
        return configuredVotes.every(vote => {
            const hasTitle = vote.title && vote.title.trim();
            const hasValidOptions = vote.options && vote.options.length >= 2;
            const allOptionsValid = vote.options.every(opt => !!getVoteOptionContent(opt));
            return hasTitle && hasValidOptions && allOptionsValid;
        });
    }

    /**
     * 整体数据是否可提交
     */
    @computed get canSubmit() {
        return this.isPostDataValid && this.isVoteDataValid && !this.loading.submit;
    }
}

export { initPostData };
export { getConfiguredVotes };
export default SetPostStore;
